import React, { useContext, useEffect, useState } from 'react';
import {
    makeStyles, useId, shorthands, tokens,
    Toolbar, ToolbarButton, ToolbarGroup,
    Dialog, DialogTrigger, DialogSurface, DialogTitle, DialogBody, DialogActions, DialogContent,
    Dropdown, Option, DropdownProps,
    Button,
} from '@fluentui/react-components';
import { Tooltip } from '@fluentui/react-components';
import {
    ArrowClockwiseFilled,
    ArrowShuffleFilled,
    LightbulbFilamentRegular,
    QuestionFilled,
    SettingsRegular,
    PlayingCardsRegular,
} from '@fluentui/react-icons';
import { GameSettingsContext, SkillLevel } from './BridgeEngineSettings';
import { PlayStateContext } from './BridgeEnginePlay';
import DialogSetup from './DialogSetup';
import DialogHelp from './DialogHelp';
import './BridgeControls.css';

//TODO: remove or correct this.
const useStyles = makeStyles({
    dropdown: {
        maxWidth: '10em',
        minWidth: '10em',
        ...shorthands.padding(0),
        fontSize: tokens.fontSizeBase200,
        '& button': {
            fontSize: 'inherit',
        },
    },
    listbox: {
        '&&': {
            fontSize: `${tokens.fontSizeBase200}`,
            '& .fui-Option': {
                fontSize: 'inherit',
                padding: '0em',
            },
        },
    },
});

const BridgeControls: React.FC = () => {
    const gameSettingsStateContext = useContext(GameSettingsContext)!;
    const { gameSettings, updateGameSettings } = gameSettingsStateContext;

    const playStateContext = useContext(PlayStateContext)!;
    const [turnState, setTurnState] = useState(playStateContext.turnState);
    const [boardState, setBoardState] = useState(playStateContext.boardState);

    const [isHelpDialogVisible, setIsHelpDialogVisible] = useState(false);
    const [isSetupDialogVisible, setIsSetupDialogVisible] = useState(false);

    useEffect(() => {
        setTurnState(playStateContext.turnState);
    }, [playStateContext.turnState]);

    useEffect(() => {
        setBoardState(playStateContext.boardState);
    }, [playStateContext.boardState]);

    const handleHintButtonClick = () => {
        if (!turnState.leadShown) {
            playStateContext.showLead();
            return;
        }
        playStateContext.showHint();
    };

    const isLeadOrHint = (): string => {
        if (!turnState.leadShown && boardState.lead.length > 0) {
            return 'the lead';
        }
        if (turnState.hintsShown < boardState.hints.length && boardState.hints[turnState.hintsShown].length > 0) {
            return 'a hint';
        }
        return '';
    }

    const handlePlaceCardButtonClick = () => {
        playStateContext.showRandomCard();
    };

    const handleGiveUpButtonClick = () => {
        playStateContext.giveUp();
    };

    const handleChangeSkillLevel: DropdownProps['onOptionSelect'] = (ev, data) => {
        const newSkillLevel = data.optionValue as unknown as SkillLevel;
        updateGameSettings({ skillLevel: newSkillLevel });
        playStateContext.newGame();
    };

    const handleNewGameButtonClick = () => {
        playStateContext.newGame();
    };

    const handleSettingsClick = () => {
        setIsSetupDialogVisible(true);
    };

    const handleHelpClick = () => {
        setIsHelpDialogVisible(true);
    };

    const closeDialog = () => {
        setIsHelpDialogVisible(false);
        setIsSetupDialogVisible(false);
    };

    const SkillLevels = Object.values(SkillLevel);
    const SkillLevelStringMap: Record<SkillLevel, string> = {
        [SkillLevel.Beginner]: 'Beginner',
        [SkillLevel.Intermediate]: 'Intermediate',
        [SkillLevel.Advanced]: 'Advanced',
        [SkillLevel.LifeMaster]: 'Life Master',
        [SkillLevel.Expert]: 'Expert',
        [SkillLevel.WorldClass]: 'World Class'
    };
    
    // for responsive design.
    const fontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
    const styles = useStyles();
    const isRandomTooltipProps = !playStateContext.isRandomCardAvailable() ? { visible: false } : {};

    return (
        <>
            {isHelpDialogVisible && <DialogHelp handleOnClose={closeDialog} />}
            {isSetupDialogVisible && <DialogSetup handleOnClose={closeDialog} />}

            <Toolbar className='controls-toolbar'>
                <ToolbarGroup>
                    <Tooltip content='Change skill level' relationship='label'>
                        <Dropdown
                            id='dropdownId'
                            className={styles.dropdown}
                            selectedOptions={[SkillLevelStringMap[gameSettings.skillLevel]]}
                            onOptionSelect={handleChangeSkillLevel}
                            value={SkillLevelStringMap[gameSettings.skillLevel]}
                            listbox={{ className: styles.listbox }}
                        >
                            {SkillLevels.map((level: SkillLevel) => (
                                <Option key={level}
                                    value={level}
                                    disabled={level === SkillLevel.WorldClass}
                                >
                                    {SkillLevelStringMap[level]}
                                </Option>
                            ))}
                        </Dropdown>
                    </Tooltip>
                    {isLeadOrHint() !== '' ? (
                        <Tooltip content={`Show ${isLeadOrHint()}`} relationship='label'>
                            <ToolbarButton
                                icon={<LightbulbFilamentRegular />}
                                onClick={handleHintButtonClick}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip content='No more hints' relationship='label'>
                            <ToolbarButton
                                icon={<LightbulbFilamentRegular />}
                                disabled
                            />
                        </Tooltip>
                    )}
                    {playStateContext.isRandomCardAvailable() ? (
                        <Tooltip content='Place a random card' relationship='label'>
                            <ToolbarButton
                                icon={<PlayingCardsRegular />}
                                onClick={handlePlaceCardButtonClick}
                            />
                        </Tooltip>
                    ) : (
                        <Tooltip content='No more cards' relationship='label'>
                            <ToolbarButton
                                icon={<PlayingCardsRegular />}
                                disabled
                            />
                        </Tooltip>
                    )}
                </ToolbarGroup>
                <ToolbarGroup>
                    <Tooltip content='Give up' relationship='label'>
                        <ToolbarButton
                            // icon={<ArrowDownExclamationFilled />}
                            icon={<ArrowShuffleFilled />}
                            onClick={handleGiveUpButtonClick}
                        />
                    </Tooltip>
                    <Tooltip content='New game' relationship='label'>
                        <ToolbarButton
                            icon={<ArrowClockwiseFilled />}
                            onClick={handleNewGameButtonClick}
                        />
                    </Tooltip>
                    <Tooltip content='Game settings' relationship='label'>
                        <ToolbarButton
                            icon={<SettingsRegular />}
                            onClick={handleSettingsClick}
                        />
                    </Tooltip>
                    <Tooltip content='How to play' relationship='label'>
                        <ToolbarButton
                            icon={<QuestionFilled />}
                            onClick={handleHelpClick}
                        />
                    </Tooltip>
                </ToolbarGroup>
            </Toolbar>
        </>
    );
};

export default BridgeControls;